import { z } from 'zod';

/**
 * Base response schema for API responses
 * Contains the success flag common to all API responses
 */
export const BaseResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Error response schema for failed API requests
 * Used when success: false
 * Optional errCode for specific error handling (e.g., ADDRESS_CONNECTED_TRANSACTIONS)
 */
export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  errCode: z.number().optional(),
});

/**
 * Success message schema for successful API requests
 * Used when success: true with a message
 */
export const SuccessMessageSchema = z.object({
  success: z.literal(true),
  message: z.string(),
});

/**
 * Generic success response schema with data payload
 * Used for responses that return data
 */
export const SuccessResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
  });

/**
 * Next.js getServerSideProps return structure schema (for testing)
 * Validates that getServerSideProps returns the expected { props: {...} } shape
 */
export const GetServerSidePropsReturnSchema = z.object({
  props: z.object({}).passthrough(),
});

export type GetServerSidePropsReturn = z.infer<typeof GetServerSidePropsReturnSchema>;

/**
 * Inferred TypeScript types from base schemas
 */
export type BaseResponse = z.infer<typeof BaseResponseSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type SuccessMessage = z.infer<typeof SuccessMessageSchema>;
export type SuccessResponse<T> = {
  success: true;
  data: T;
};
